import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { aiApi } from '../../../api/ai.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { PersonalityFormModal } from './PersonalityFormModal.jsx';
import { PersonalityAssignments } from './PersonalityAssignments.jsx';

const TONE_LABEL = {
  friendly: 'Amichevole', formal: 'Formale', playful: 'Giocoso',
  empathic: 'Empatico', technical: 'Tecnico'
};

export function PersonalityPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canManage = hasPermission('ai:configure');

  const { items, isLoading, pagination, setPage } = usePaginatedQuery({
    key: ['ai', 'personalities'],
    fetcher: aiApi.listPersonalities
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const activateMutation = useMutation({
    mutationFn: (id) => run(() => aiApi.activatePersonality(id)),
    onSuccess: () => {
      toast.success('Personalità impostata come default.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'personalities'] });
      setActivateTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => aiApi.deletePersonality(id)),
    onSuccess: () => {
      toast.success('Personalità eliminata.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'personalities'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Nome' },
    {
      key: 'tone',
      header: 'Tono',
      render: (r) => <Badge>{TONE_LABEL[r.tone] || r.tone}</Badge>
    },
    {
      key: 'language',
      header: 'Lingua',
      render: (r) => <span className="text-xs text-ink-muted uppercase">{r.language || '-'}</span>
    },
    {
      key: 'version',
      header: 'Versione',
      align: 'right',
      render: (r) => <span className="text-xs text-ink-muted">{r.version ?? '-'}</span>
    },
    {
      key: 'estimatedPromptTokens',
      header: 'Token (stima)',
      align: 'right',
      render: (r) => (
        <span className="text-xs text-ink-muted" title="Token aggiunti a ogni richiesta: persona + conversazione di esempio (preambolo di sicurezza escluso)">
          ~{(r.estimatedPromptTokens ?? 0).toLocaleString('it-IT')}
        </span>
      )
    },
    {
      key: 'isActive',
      header: 'Default',
      render: (r) => r.isActive ? <Badge tone="success">Default</Badge> : <span className="text-xs text-ink-faint">-</span>
    },
    {
      key: 'updatedAt',
      header: 'Aggiornata',
      render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.updatedAt || r.createdAt)}</span>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canManage && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canManage && !r.isActive && (
            <button type="button" className="text-xs text-brand hover:text-brand-hover" onClick={() => setActivateTarget(r)}>
              Imposta come default
            </button>
          )}
          {canManage && !r.isActive && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.aiPersonality')}
        description="Tono e identità dell'assistente AI. Una versione è quella predefinita (default); puoi inoltre assegnare personalità diverse a ciascun compito qui sotto."
        actions={canManage ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuova personalità</Button> : null}
      />

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessuna personalità', description: 'Crea la prima personalità per dare voce all\'assistente.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <PersonalityAssignments canManage={canManage} />

      <PersonalityFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
      />

      <ConfirmDialog
        open={Boolean(activateTarget)}
        title="Impostare come default?"
        description={activateTarget ? `La versione "${activateTarget.name}" diventerà quella predefinita al posto di quella attuale. Servirà una verifica MFA recente.` : ''}
        confirmLabel="Imposta come default"
        loading={activateMutation.isPending}
        onCancel={() => setActivateTarget(null)}
        onConfirm={() => activateMutation.mutate(activateTarget._id)}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare la personalità?"
        description={confirmTarget ? `La versione "${confirmTarget.name}" sarà rimossa. Operazione irreversibile.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
